import { createTheme } from '@mui/material/styles'
import type {} from '@mui/x-data-grid-pro/themeAugmentation'
import type {} from '@mui/x-date-pickers/themeAugmentation'
import { colors } from 'src/constants/colors'

function getCssVariable(name: string) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}

export const theme = createTheme({
  cssVariables: true,
  palette: {
    primary: {
      main: getCssVariable('--mui-primary-500'),
    },
    secondary: {
      main: getCssVariable('--mui-shades-900'),
    },
    error: {
      main: getCssVariable('--mui-critical-500'),
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        'html, body, #root': {
          fontFamily: 'Geist, sans-serif',
          letterSpacing: 0,
        },
        'button, input, select, textarea': {
          fontFamily: 'inherit',
        },
      },
    },
    // Ported from apps/dashboard's theme so every GridTable reads the same: 14px grey text in
    // header and cells, purple-25 row hover, hidden column separators, a quiet header hover that
    // reveals the column menu, and a thin custom scrollbar. Font follows this app (Geist).
    MuiDataGrid: {
      styleOverrides: {
        root: {
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          '& .MuiDataGrid-withBorderColor': {
            borderColor: colors.shades[100],
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: colors.primary[25],
          },
          '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: colors.primary[25],
          },
          '& .MuiDataGrid-row.Mui-selected:hover': {
            backgroundColor: colors.primary[25],
          },
          // v8 pads every row with a `scrollbarFiller` (the vertical scrollbar's width) painted in
          // the header's white, which reads as a gap beside a hovered row. Transparent, it shows
          // the row's own background instead.
          '& .MuiDataGrid-row .MuiDataGrid-scrollbarFiller': {
            backgroundColor: 'transparent',
          },
          // Pinned cells paint their own background so content scrolls under them; on hover they
          // must match the row tint above rather than MUI's default grey mix.
          '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight': {
            backgroundColor: colors.shades[0],
          },
          '& .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedRight':
            {
              backgroundColor: colors.primary[25],
            },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontFamily: 'Geist, sans-serif',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: '20px',
            color: colors.shades[500],
          },
          '& .MuiDataGrid-cell': {
            fontFamily: 'Geist, sans-serif',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 400,
            // No lineHeight here: v8 centres plain-text cells with a line-height equal to the
            // row height (`calc(var(--height) - 1px)`), so pinning 20px pushes the text to the
            // top. Flex cells (`renderCell`) are unaffected either way.
            color: colors.shades[500],
            paddingLeft: '16px',
          },
          '& .MuiDataGrid-cellCheckbox': {
            paddingLeft: '0px',
          },
          // v8 closes each row with an empty filler cell that takes the leftover width. The cell
          // padding above would keep it at least 16px wide, so flex columns that fill the grid
          // overflowed it by 16px and every such grid scrolled sideways.
          '& .MuiDataGrid-cellEmpty': {
            paddingLeft: '0px',
          },
          // v8 draws a 1px rule on the inner edge of a pinned column, and the header's copy of it
          // lands a scrollbar-width away from the body's. The sticky white background already
          // separates the pinned column from what scrolls under it, so neither rule is needed.
          '& .MuiDataGrid-cell--withLeftBorder, & .MuiDataGrid-cell--withRightBorder, & .MuiDataGrid-columnHeader--withLeftBorder, & .MuiDataGrid-columnHeader--withRightBorder':
            {
              borderLeftColor: 'transparent',
              borderRightColor: 'transparent',
            },
          // 68 = 36px trigger + 16px either side; MUI's default right padding is 10.
          '& .MuiDataGrid-cell[data-field="actions"]': {
            paddingRight: '16px',
          },
          '& .MuiDataGrid-columnSeparator': {
            color: 'transparent',
          },
          '& .MuiDataGrid-columnHeader': {
            paddingLeft: '16px',
            '&:hover': {
              backgroundColor: colors.shades[50],
              borderRadius: '4px',
            },
            '&:hover .MuiDataGrid-columnHeaderTitle': {
              color: colors.shades[400],
            },
            '&:hover .MuiDataGrid-columnSeparator': {
              color: colors.shades[200],
            },
            '&:hover .MuiDataGrid-menuIcon': {
              visibility: 'visible',
              width: 'auto',
            },
            '&:hover .MuiDataGrid-menuIconButton': {
              opacity: 1,
            },
          },
          '& .MuiDataGrid-menuIcon': {
            marginRight: '-4px',
          },
          '& .MuiDataGrid-menuIconButton': {
            padding: '4px',
            borderRadius: '6px',
            color: colors.shades[300],
            '&:hover': {
              backgroundColor: colors.shades[100],
              color: colors.shades[300],
            },
          },
          '& .MuiDataGrid-columnHeaderCheckbox': {
            paddingLeft: '0px',
          },
          // Unlike the dashboard's v6 grid, v8 hides this element's native scrollbar and draws
          // its own `.MuiDataGrid-scrollbar` beside it — so no scrollbar styling here, or the
          // native one comes back and the grid shows two.
          '& .MuiDataGrid-virtualScroller': {
            // Disable macOS elastic overscroll: without this the body rubber-bands past
            // the pinned columns and detaches from the header on trackpad flicks.
            overscrollBehavior: 'none',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          height: '56px',
          borderRadius: '8px',
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
      },
    },
    // Inputs follow apps/dashboard's theme, not the client app's: a 36px single-line field with
    // 14px text and an 8px radius, multiline fields growing from an 8/12px padding box.
    MuiInputBase: {
      styleOverrides: {
        root: { borderRadius: '8px', height: 36 },
        input: {
          fontFamily: 'Geist, sans-serif',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 400,
          letterSpacing: '0px',
          lineHeight: '20px',
        },
        multiline: {
          height: 'auto',
        },
      },
    },
    // MUI X pickers render their own field (`MuiPickersInputBase` / `MuiPickersOutlinedInput`),
    // not `MuiInputBase`, so the 36px / 8px-radius / 14px rules above do not reach them without
    // this mirror. Keeps a date or time picker the same box as the text field beside it.
    MuiPickersInputBase: {
      styleOverrides: {
        root: {
          height: 36,
          borderRadius: '8px',
          fontFamily: 'Geist, sans-serif',
          fontSize: '14px',
          fontWeight: 400,
          letterSpacing: '0px',
          lineHeight: '20px',
        },
        sectionContent: {
          fontFamily: 'Geist, sans-serif',
          fontSize: '14px',
          lineHeight: '20px',
        },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: '8px' },
        notchedOutline: { borderColor: getCssVariable('--mui-shades-300') },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: '8px' },
        multiline: {
          padding: 8,
          paddingLeft: 12,
          paddingRight: 12,
        },
        inputMultiline: {
          padding: '0px',
        },
        notchedOutline: { borderColor: getCssVariable('--mui-shades-300') },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontFamily: 'Geist, sans-serif',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 400,
          letterSpacing: '0px',
          lineHeight: '20px',
        },
      },
    },
  },
  typography: {
    fontFamily: 'Geist, sans-serif',
    body1: {
      fontFamily: 'Geist, sans-serif',
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: 400,
      letterSpacing: '0px',
      lineHeight: '22px',
    },
    body2: {
      fontFamily: 'Geist, sans-serif',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 400,
      letterSpacing: '0px',
      lineHeight: '20px',
    },
    button: {
      fontFamily: 'Geist, sans-serif',
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: 600,
      letterSpacing: '0.32px',
      lineHeight: '20px',
    },
    caption: {
      fontFamily: 'Geist, sans-serif',
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: 500,
      letterSpacing: '0px',
      lineHeight: '22px',
    },
    h1: {
      fontFamily: 'Geist, sans-serif',
      fontSize: '34px',
      fontStyle: 'normal',
      fontWeight: 700,
      letterSpacing: '-0.68px',
      lineHeight: '42px',
    },
    h2: {
      fontFamily: 'Geist, sans-serif',
      fontSize: '28px',
      fontStyle: 'normal',
      fontWeight: 700,
      letterSpacing: '-0.56px',
      lineHeight: '32px',
    },
    h3: {
      fontFamily: 'Geist, sans-serif',
      fontSize: '20px',
      fontStyle: 'normal',
      fontWeight: 700,
      letterSpacing: '-0.44px',
      lineHeight: '26px',
    },
    h4: {
      fontFamily: 'Geist, sans-serif',
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: 700,
      letterSpacing: '0px',
      lineHeight: '22px',
    },
    h5: {
      fontFamily: 'Geist, sans-serif',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 700,
      letterSpacing: '0px',
      lineHeight: '20px',
    },
    h6: {
      fontFamily: 'Geist, sans-serif',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 600,
      letterSpacing: '0px',
      lineHeight: '20px',
    },
    // inherit: {
    //   fontFamily: 'Geist, sans-serif',
    //   fontSize: '16px',
    //   fontStyle: 'normal',
    //   fontWeight: 500,
    //   letterSpacing: '0px',
    //   lineHeight: '22px',
    // },
    overline: {
      fontFamily: 'Geist, sans-serif',
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: 700,
      letterSpacing: '0.24px',
      lineHeight: '16px',
    },
    subtitle1: {
      fontFamily: 'Geist, sans-serif',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: '20px',
    },
    subtitle2: {
      fontFamily: 'Geist, sans-serif',
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: 500,
      letterSpacing: '0px',
      lineHeight: '16px',
    },
  },
})
